// AYSO Region 13 design tokens — the TypeScript-facing copy.
//
// Read this for the *narrative*: what each brand color means, and the rules
// around it. For the values a build actually consumes, the source of truth is
// `packages/ui/tailwind-preset.js` (brand palette + the OKLCH neutral scale +
// the type stack, shared by all five SPAs). This file exists because some code
// needs a raw hex rather than a Tailwind class — e.g. the fields map canvas
// paints MapLibre layers with `brandColors.red`.
//
// The two copies of the palette are pinned together by
// `packages/ui/src/tailwindPreset.test.ts`, so a divergence fails the build
// instead of drifting silently. Everything in here that is NOT covered by that
// test is narrative, and has drifted before — keep it honest.
//
// Brand origin: /Users/matthew/dev/ayso-website (the public marketing site).
// See docs/design-system.md for how its "loud at the door, calm inside"
// guidance translates to a dense admin tool.

/**
 * Brand color palette. Keys match the Tailwind names used by the public
 * site (`brand-red`, `brand-cream`, etc.) so component code reads the same
 * across both projects.
 */
export const brandColors = {
  /** Coral accent (#f74b4b). Two-tone underline strips, button accent
   *  strips. Fails WCAG AA on white text — never under body copy. */
  'red': '#f74b4b',
  /** Burgundy (#83312d). Primary text emphasis, headings, links, hero
   *  buttons, pool/division headers in the admin grid. */
  'red-dark': '#83312d',
  /** Very dark maroon (#230511). InLeague pill button background; we use
   *  it for primary admin chips like "Live", "Published". */
  'header': '#230511',
  /** Structural frame (#8e2929). Quick-action bars, sidebar headers,
   *  body link hover. The admin top-nav uses this. */
  'maroon': '#8e2929',
  /** Photo gallery strip background (#3a0d12). In admin: hero or section
   *  background where we want a darker maroon than `maroon`. */
  'maroon-dark': '#3a0d12',
  /** Warm off-white surface (#ede8e2). Admin canvas background; sidebar
   *  hover states. Replaces the usual gray-50 you'd see in shadcn defaults. */
  'cream': '#ede8e2',
  /** Field "Open" (#a6ce57). In admin: success badges, "Healthy", "OK". */
  'green': '#a6ce57',
  /** Reserved (#007a32) — not currently on the public site. Keep available. */
  'green-dark': '#007a32',
  /** Reserved (#00d050) — not currently on the public site. Keep available. */
  'green-light': '#00d050',
  /** Field "Monitoring", announcement bars (#f4bd4d). In admin: warning
   *  badges, registration-deadline reminders, soft-violation banners. */
  'gold': '#f4bd4d',
  /** Legible amber (#a1701a). `gold` is a *wash and fill* color: at 1.62:1 on
   *  its own 10% tint it cannot carry an icon or a word. Reach for this
   *  wherever a warning has to be READ — the `Callout` warn icon, warning text
   *  on a gold surface. 4.08:1 on the gold/10 wash, 4.30:1 on white, so it
   *  clears non-text contrast with margin and body text too. */
  'gold-dark': '#a1701a',
  /** Body text and footer background (#221f1f). Admin nav text, primary
   *  body text, audit-log timestamps. */
  'dark': '#221f1f',
} as const

export type BrandColorName = keyof typeof brandColors

/** Semantic role mapping for the **brand accents** — what each brand color
 *  means when you reach for "danger" or "success" in a component.
 *
 *  Scope note: surfaces, body text, and hairlines are NOT brand colors. They
 *  are the OKLCH neutral scale (`--canvas` / `--surface` / `--line` / `--ink` /
 *  `--ink-soft` / `--ink-mut` / `--fill` / `--fill-hover`), defined once in
 *  `packages/ui/tailwind-preset.js` and reached as Tailwind `bg-canvas`,
 *  `text-ink`, `border-line`, etc. Use those, not a literal here. (`brand-cream`
 *  was the page surface before the neutral scale landed; today it survives only
 *  in the `.section-header` strip.) */
export const brandRoles = {
  /** Darkest maroon field — hero or section background where `maroon` is too light. */
  surfaceDeep: brandColors['maroon-dark'],

  primary: brandColors['red-dark'],   // action-bar headings, primary buttons
  primaryAlt: brandColors['maroon'],  // links on hover, structural frames
  accent: brandColors['red'],         // accent strips, two-tone underlines

  textHeading: brandColors['red-dark'],

  success: brandColors['green'],
  warning: brandColors['gold'],
  danger: brandColors['red-dark'],    // dark red for text-bearing surfaces
  info: brandColors['maroon'],

  borderStrong: brandColors['maroon'],
} as const

/** Typography. **Hanken Grotesk is the workhorse face on every admin/app
 *  surface** (scheduler admin, console, referee, fields, coach) and is what the
 *  public schedule body uses too; each SPA loads it from Google Fonts at
 *  weights 400–800. Raleway is now confined to the *marketing chrome* the
 *  public SSR site shares with www.ayso13.org (the footer + philosophies
 *  bands, `lib/layout.ts`). Do not reach for Raleway in an admin surface.
 *
 *  Kept in step with the Tailwind stack in packages/ui/tailwind-preset.js by
 *  `tailwindPreset.test.ts`. */
export const typography = {
  fontFamily: {
    sans: ['"Hanken Grotesk"', 'system-ui', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'].join(','),
    /** For tabular data (matchup grids, scores). Keeps the rest in Hanken. */
    mono: ['"JetBrains Mono"', 'ui-monospace', 'Menlo', 'monospace'].join(','),
  },
  /** Loaded weights. 300 and 900 are NOT requested by any surface's font link
   *  (400–800), so asking for them silently renders a synthesized face. */
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  letterSpacing: {
    tight: '-0.025em',  // big bold display headings
    wide: '0.05em',     // uppercase chip labels
    wider: '0.1em',     // section labels in the action bar
  },
} as const

/** Border radii. The public site uses sharp / minimal radius — keep that
 *  feel; never reach for ✨ rounded-3xl card aesthetics. */
export const radius = {
  none: '0',
  sm: '2px',     // chips, badges, small inputs
  md: '6px',     // buttons, inputs
  lg: '12px',    // cards, dialog surfaces (the maximum on this site)
  pill: '9999px',// only for "inLeague-style" pill buttons
} as const

/** The public marketing site's signature move: solid body + offset stripe of a
 *  complementary brand color, alternating direction across paired CTAs.
 *
 *  **Public site only — no admin surface reads this.** The admin deliberately
 *  stays calm: its lifecycle CTAs are the solid `.btn-primary`. Kept here to
 *  document the brand pattern. See docs/design-system.md "two-tone offset". */
export const twoToneOffset = {
  /** Stripe height for the offset bar. The public site uses h-1.5 (6px). */
  stripeClass: 'h-1.5',
  /** First variant — body red, stripe red-dark. Use for primary CTAs (e.g. Optimize, Publish). */
  primaryBodyClass: 'bg-brand-red text-white font-bold uppercase tracking-wider',
  primaryStripeClass: 'h-1.5 bg-brand-red-dark',
  /** Second variant — body red-dark, stripe red. Use as the *paired* CTA
   *  next to the primary (e.g. "Optimize" + "Publish" sit together). */
  pairedBodyClass: 'bg-brand-red-dark text-white font-bold uppercase tracking-wider',
  pairedStripeClass: 'h-1.5 bg-brand-red',
} as const

/** Button presets — the canonical **class names**, not copies of their
 *  declarations. Each is defined once, in `@region13/ui/tailwind-preset.js`;
 *  reach for the class (or `Button` / `BUTTON_CLASSES` in `@region13/ui`),
 *  never a hand-rolled equivalent. One solid button per view: primary, or
 *  warning when the action leaves the platform.
 *
 *  This used to hold duplicated Tailwind strings, which drifted from the real
 *  CSS (stale `px-6 py-3 rounded-lg` against the actual `px-5 py-2.5
 *  rounded-md`, and `text-brand-red` where the CSS correctly uses
 *  `brand-red-dark` — the copy here was recommending the AA failure that
 *  `brandColors.red` warns about two dozen lines up). Naming the class removes
 *  the copy that could drift. */
export const buttonPresets = {
  /** The view's one main internal action: white on red-dark. */
  primary: 'btn-primary',
  /** The main action when it leaves the platform (a live send, an InLeague
   *  push, a publish): gold body, dark text. */
  warning: 'btn-warning',
  /** Every other action, including test sends, previews, exports and cancels. */
  secondary: 'btn-secondary',
  /** A trigger that starts a destructive action (delete, remove, clear). */
  danger: 'btn-danger',
} as const

/** Anti-references — what NOT to do, lifted verbatim from the public
 *  site's .impeccable.md. Encoded as exported strings so anyone reaching
 *  for one of these patterns gets a hit in code review / search. */
export const antiPatterns = [
  'glassmorphism',
  'backdrop-blur',
  'gradient-text',
  'glow-effect',
  'side-stripe-accent-border',
  'rounded-3xl-card',
  'kid-fonts',
  'clip-art-soccer-balls',
  'illustrated-mascots',
  'stock-photography',
  'corporate-league-polish',
] as const
